<template>
  <page :loading="$apollo.queries.job.loading || !job || isLoading" :err="err" layoutClass="internal-job-show">

    <b-row v-if="job" class="mb-3">
      <b-col :class="{ 'p-2': true, 'px-md-4': isRecruiter, 'pl-md-4': !isRecruiter }" :md="isRecruiter ? 12 : 9">

        <!-- job details card -->
        <b-card>

          <!-- image -->
          <!-- img
            v-if="job.image"
            class="w-100"
            style="max-height: 350px"
            :src="job.image"
          / -->
          <b-row class="mt-3">

            <!-- title and location -->
            <b-col class="px-3" md="8">
              <h4 style="font-weight: 500">
                <span v-if="job.location">
                  <font-awesome-icon class="text-muted" icon="map-marker"/> {{ job.location.name }}, {{ job.location.countryCode }}
                </span>
                <span v-if="job.customFieldsHash && job.customFieldsHash.contract" class="ml-3">
                  <font-awesome-icon class="text-muted" icon="clock"/> {{ job.customFieldsHash.contract }}
                </span>
              </h4>
              <h3><strong>{{ job.title }}</strong><span
                v-if="job.isDevelopment"
                class="development-tag ml-3"
                :style="{ backgroundColor: developmentMatchBG }"><font-awesome-icon style="font-size: 13px" icon="signal"/> Development</span></h3>
            </b-col>

            <!-- buttons -->
            <b-col class="px-3" md="4">
              <template v-if="job.status === 'ACTIVE' || isRecruiter">
                <b-button
                  v-if="isRecruiter"
                  class="float-right ml-2"
                  variant="primary"
                  :to="{ name: 'JobEdit', params: { jobID: job.id } }"
                >
                  <font-awesome-icon icon="pencil-alt"/> Edit
                </b-button>
                <b-button
                  v-if="!job.isInternal && $root.isReferralsFeatureEnabled && !isRecruiter"
                  class="float-right"
                  variant="primary"
                  :to="{ name: 'JobShow', params: { jobID: job.id } }"
                >
                  <font-awesome-icon icon="link"/> Refer a Contact
                </b-button>
              </template>
            </b-col>
          </b-row>

          <!-- description -->
          <markdown v-if="showFullDescription || job.status !== 'ACTIVE'" :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
          <markdown v-else :source="job.excerpt" :anchorAttributes="markdownAnchorAttributes"/>

          <!-- skills -->
          <div v-if="skills" class="pb-3">
            <span
              class="skill-tag mr-2 mt-1"
              v-for="skill in skills"
              :style="{ backgroundColor: skill.background }"
              :key="skill.name"
              v-b-tooltip.hover :title="skill.tooltip ? skill.tooltip : ''"
            >{{ skill.name }}</span>
          </div>

          <!-- toggle description -->
          <a href="#" class="py-2" @click.prevent="toggleDescription">
            <template v-if="!showFullDescription">Show Description</template>
            <template v-else>Hide Description</template>
          </a>

          <!-- dates -->
          <div
            v-if="job.project && job.project.startDate && job.project.endDate"
            class="d-flex justify-content-between align-items-center my-3"
          >
            <span><span class="text-muted">Start</span> <b>{{ formatDate(job.project.startDate, 'DD.MM.YYYY') }}</b></span>
            <font-awesome-icon icon="long-arrow-alt-right" />
            <span><span class="text-muted">End</span> <b>{{ formatDate(job.project.endDate, 'DD.MM.YYYY') }}</b></span>
          </div>

          <!-- project -->
          <div class="mt-3 pt-3 border-top" v-if="job.project">
            <p><b>Project: {{ job.project.title }}</b></p>
            <p v-if="job.project.excerpt">{{ job.project.excerpt }}</p>
            <b-link
              variant="primary-link"
              :to="{ name: 'ProjectShow', params: { projectID: job.project.id } }"
            >View Project</b-link>
          </div>

        </b-card>

        <!-- apply now card -->
        <b-alert show variant="success" v-if="applyResult === 'OK'" class="mt-3">
          The application has been submitted. Thank you!
        </b-alert>
        <b-alert show variant="danger" v-if="applyResult === 'ERR'" class="mt-3">
          Sorry an error has occurred, please try again.
        </b-alert>
        <job-application-form
          v-if="!isRecruiter && !job.customFieldsHash.applicationFormURL"
          :value="application"
          @input="applySubmit($event)"
        />
        <b-card v-else-if="!isRecruiter && job.customFieldsHash.applicationFormURL" class="mt-4">
          <b-row>
            <b-col class="p-5 d-flex justify-content-center">
              <b-button :href="job.customFieldsHash.applicationFormURL" target="_blank">Apply Now</b-button>
            </b-col>
          </b-row>
        </b-card>

        <b-tabs content-class="mt-3" v-if="isRecruiter && job.status === 'ACTIVE'" class="mt-4">
            <b-tab title="Talent Pool" active>
              <!-- matched users card -->
              <b-card v-if="Array.isArray(talents)">
                <internal-talent-pool-container
                  class="mt-4"
                  :talents="talents"
                  :job="job"
                  @sendContactEmail="sendContactEmail($event)"
                />
              </b-card>
            </b-tab>
            <b-tab title="Apply Now">
              <job-application-form
                v-if="!job.customFieldsHash.applicationFormURL"
                :value="application"
                @input="applySubmit($event)"
              />
              <b-card v-else>
                <b-row>
                  <b-col class="p-5 d-flex justify-content-center">
                    <b-button :href="job.customFieldsHash.applicationFormURL" target="_blank">Apply Now</b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-tab>
        </b-tabs>

        <template v-else-if="isRecruiter && job.status !== 'ACTIVE'" class="mt-4">
          <b-card class="mt-3">
            <b-alert v-if="job.status === 'INACTIVE'" show variant="warning">Sorry, this job has been closed.</b-alert>
            <b-alert v-else-if="job.status === 'DELETED'" show variant="warning">Sorry, this job has been deleted.</b-alert>
          </b-card>
        </template>

      </b-col>

      <!-- matched mentors card -->
      <b-col v-if="!isRecruiter" class="px-md-2 pr-md-4 p-2" md="3">
        <b-card>
          <b-row class="pb-3 border-bottom">
            <b-col v-if="!job.isDevelopment">
              <span class="block-title pb-2">Get help to prepare</span><br>
              Mentors and project roles that may help you to prepare for this opportunity.
            </b-col>
            <b-col v-else class="block-title">Find more help here</b-col>
          </b-row>
          <b-row v-if="company.sitePreferences.externalLearningPlatformText">
            <b-col class="pt-3">
              <markdown
                :source="company.sitePreferences.externalLearningPlatformText"
                :anchorAttributes="markdownAnchorAttributes"
              />
            </b-col>
          </b-row>
          <b-row class="px-2 mt-3">
            <b-col class="px-1">
              <b-nav tabs justified>
                <b-nav-item
                  :active="selectedSuggestion === 'mentors'"
                  @click="selectedSuggestion = 'mentors'"
                >Mentors</b-nav-item>
                <b-nav-item
                  :active="selectedSuggestion === 'roles'"
                  @click="selectedSuggestion = 'roles'"
                >Projects</b-nav-item>
              </b-nav>
            </b-col>
          </b-row>
          <b-row>
            <suggested-mentors
              v-show="selectedSuggestion === 'mentors'"
              :mentors="job.imMatchedMentors"
              :company="company"
              :user="user"
              :job="job"
              :count="3"
              layout="vertical"
              @submitContactMentorRequest="submitContactMentorRequest($event)"
            />
            <suggested-project-jobs
              v-show="selectedSuggestion === 'roles'"
              :jobs="Array.isArray(job.imMatchedRoles) ? job.imMatchedRoles.slice(0,3) : []"
              :matchedJob="job"
              layout="vertical"
              cardLayout="minimal"
              :isProjectDisplay="false"
            />
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <b-modal ref="testEmailSentSucessfullyModal" id="testEmailSentSucessfullyModal" title="Test Email Sent Successfully" body-class="py-5" hide-header>
      <p class="feedback-icon-line success"><font-awesome-icon icon="check"/></p>
      <p class="text-dark feedback-text-line">The email has been successfully sent.</p>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('testEmailSentSucessfullyModal')">OK</b-button>
      </template>
    </b-modal>

    <b-modal ref="emailSendErrorModal" id="emailSendErrorModal" title="Error Sending Emails" body-class="py-5" hide-header>
      <p class="feedback-icon-line error"><font-awesome-icon icon="times"/></p>
      <p class="text-dark feedback-text-line">Unfortunately, we coud not send your email.</p>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('emailSendErrorModal')">OK</b-button>
      </template>
    </b-modal>

  </page>
</template>

<script>
import Markdown from 'vue-markdown';
import { mapRoot } from '@src/mixins/root';
import formatDate from 'date-fns/format';
import tinycolor from 'tinycolor2';

import Page from '@/components/Page';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import JobApplicationForm from '@/components/jobs/InternalJobApplicationForm';
import InternalJobShowQuery from '@/graphql/queries/InternalJobShow.gql';
import UsersListMatchingQuery from '@/graphql/queries/UsersListMatching.gql';
import JobTitleSelector from '@/components/profile/JobTitleSelector';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import LocationSearch from '@/components/misc/LocationSearch';
import SuggestedProjectJobs from '@/components/dashboard/SuggestedProjectJobs.vue';
import SuggestedMentors from '@/components/dashboard/SuggestedMentors.vue';
import JobApplicationCreateMutation from '@/graphql/mutations/JobApplicationCreate.gql';
import MentorRequestCreateMutation from '@/graphql/mutations/MentorRequestCreate.gql';
import SendEmailMutation from '@/graphql/mutations/SendEmail.gql';
import {
  mentorToCurrentJobMatchScore,
  roleToCurrentJobMatchScore,
  getUserFieldValue,
  getCurrentUserFieldValue,
  parseIfPossible,
  matchDetails
} from '@src/lib/imMatching';
import InternalTalentPoolContainer from '@/components/projects/InternalTalentPoolContainer.vue';
import recommendJobEmailTemplate from '@/emails/recommendJob';
import genericContactEmailTemplate from '@/emails/genericContact';

const emailQuery = /* GraphQL */`
  query CustomTemplateQuery {
    company {
      id
      name
      domain(where: { primary: true }) {
        domain(prefix:"https://")
        emailFrom
      }
      branding {
        name
        bigLogo {
          id
          fileLocation
        }
        smallLogo {
          id
          fileLocation
        }
        emailHeaderImage {
          id
          fileLocation
        }
        headerFont
        bodyFont
        primaryColour
        secondaryColour
        backgroundColour
      }
    }
    user {
      id
      firstName
      fullName
      emailAddress
      title
    }
  }
`;

export default {
  props: {
    jobID: String,
  },
  components: {
    Page,
    LoadingSpinner,
    Markdown,
    JobTitleSelector,
    JobSkillsSelector,
    LocationSearch,
    JobApplicationForm,
    SuggestedMentors,
    InternalTalentPoolContainer,
    SuggestedProjectJobs,
  },
  data: () => ({
    err: null,
    showFullDescription: false,
    selectedSuggestion: 'mentors',
    showApplyBox: false,
    applyResult: null,
    application: {
      message: '',
      CV: null,
      termsAgreed: false,
    },
    filters: {
      jobTitle: '',
      jobSkills: null,
      location: null,
    },
    page: 1,
    isLoading: false,
  }),
  apollo: {
    job: {
      query: InternalJobShowQuery,
      variables() {
        const { jobID, isRecruiter } = this;
        return { jobID, isRecruiter };
      },
      update({ company: { job } = {}, user }) {
        console.log('JOOOB: ', JSON.parse(JSON.stringify(job)));

        if (Array.isArray(job.jobTitleSkills)) {
          job.skills = [ ...(Array.isArray(job.skills) ? job.skills : []), ...job.jobTitleSkills ]
            .reduce((out, el) => {
              if (!out.includes(el)) out.push(el);
              return out;
            }, []);
        }

        // prepare matched mentors
        if (Array.isArray(job.imMatchedMentors)) {
          const { mentorRequests } = user;
          job.imMatchedMentors = this.matchMentors(job.imMatchedMentors
            .map(el => {

              // assign mentor requests
              const mentorRequest = Array.isArray(mentorRequests) ? mentorRequests.find(({ mentor: { id: mentorID } }) => mentorID === el.mentor.id) : null;
              el.mentor.mentorRequest = {};
              if (mentorRequest) {
                el.mentor.mentorRequest = mentorRequest;
              }
              el.mentor.imMatchingDetails = el.matchingDetails;
              return el.mentor;
            }), job);
        }

        // prepare matched roles
        if (Array.isArray(job.imMatchedRoles)) {
          job.imMatchedRoles = this.matchRoles(job.imMatchedRoles
            .map(el => {
              el.role.imMatchingDetails = el.matchingDetails;
              return el.role;
            }), job);
        }

        console.log('UPDATEEEED JOOOB: ', job);
        return job;
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      prefetch: true,
    },
    talents: {
      query: UsersListMatchingQuery,
      update({ company: { userConnections } }) {
        if (userConnections && Array.isArray(userConnections.edges) && userConnections.edges.length) {
          return userConnections.edges.map(({ entry }) => {
            const { id, avatar, firstName, lastName, fullName, role, customFields, location } = entry;
            return {
              id,
              firstName,
              lastName,
              avatar,
              role,
              customFields,
              location,
              fullName: (fullName || entry.name) || '-',
              jobTitle: getUserFieldValue.call(this, entry, 'JOB_TITLES', 'skills'),
              skills: this.getSkills(entry, 3),
              rawSkills: parseIfPossible(getUserFieldValue.call(this, entry, 'JOB_SKILLS', 'skills')),
              rawDevelopmentSkills: parseIfPossible(getUserFieldValue.call(this, entry, 'JOB_DEVELOPMENT_SKILLS', 'skills')),
              user: entry,
            };
          }).filter(({ role }) => role !== 'SUPER_ADMIN');
        }
        return [];
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      prefetch: true,
      skip() {
        return !this.isRecruiter || !this.job;
      }
    },
  },
  computed: {
    ...mapRoot([
      'user', 'company', 'isLoggedIn', 'isRecruiter', 'isSuperAdmin',
    ]),
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    skills() {
      return this.getSkills();
    },
    matchBG() {
      const { siteBranding } = this.company;
      return siteBranding && siteBranding.primaryColour ? tinycolor(siteBranding.primaryColour).lighten(34).toString() : '#eee';
    },
    developmentMatchBG() {
      const { siteBranding } = this.company;
      return siteBranding && siteBranding.primaryColour ? tinycolor(siteBranding.primaryColour).lighten(42).toString() : '#eee';
    },
  },
  metaInfo() {
    if (this.job) {
      const { title } = this.job;
      return { title };
    } else {
      return { title: 'InternalJob' };
    }
  },
  methods: {
    toggleDescription() {
      this.showFullDescription = !this.showFullDescription;
      this.$scrollTo('#job-header');
    },
    getSkills(user, size) {
      const { job, matchBG, developmentMatchBG } = this;
      let userSkills = parseIfPossible(
        user ? getUserFieldValue.call(this, user, 'JOB_SKILLS', 'skills') : getCurrentUserFieldValue.call(this, 'JOB_SKILLS', 'skills')
      );
      userSkills = Array.isArray(userSkills) ? userSkills : [];
      let userDevelopmentSkills = job.isDevelopment ? parseIfPossible(
        user ? getUserFieldValue.call(this, user, 'JOB_DEVELOPMENT_SKILLS', 'skills') : getCurrentUserFieldValue.call(this, 'JOB_DEVELOPMENT_SKILLS', 'skills')
      ) : [];
      userDevelopmentSkills = Array.isArray(userDevelopmentSkills) ? userDevelopmentSkills : [];
      let skills = [];
      let developmentSkills = [];
      if (!user && Array.isArray(job.skills) && job.skills.length) {
        skills = job.skills.map(el => {
          const details = matchDetails(el, userSkills);
          const developmentMatchDetails = matchDetails(el, userDevelopmentSkills);
          return {
            matchDetails: details,
            developmentMatchDetails,
            isMatch: userSkills.includes(el),
            isDevelopmentMatch: userDevelopmentSkills.includes(el),
            background: details ? matchBG : (developmentMatchDetails ? developmentMatchBG : '#eee'),
            tooltip: details ? details.label : (developmentMatchDetails ? developmentMatchDetails.label : null),
            score: details ? details.score : (developmentMatchDetails ? developmentMatchDetails.score : null),
            name: el,
          };
        });
      }
      if (user && Array.isArray(job.skills) && job.skills.length) {
        skills = userSkills.map(el => {
          const details = matchDetails(el, job.skills);
          return {
            matchDetails: details,
            isMatch: job.skills.includes(el),
            isDevelopmentMatch: false,
            background: details ? matchBG : '#eee',
            name: el,
            tooltip: details ? details.label : null,
            score: details ? details.score : null,
          };
        });
        developmentSkills = userDevelopmentSkills.map(el => {
          const developmentMatchDetails = matchDetails(el, job.skills);
          return {
            developmentMatchDetails,
            isDevelopmentMatch: job.skills.includes(el),
            background: developmentMatchDetails ? developmentMatchBG : '#eee',
            name: el,
            tooltip: developmentMatchDetails ? developmentMatchDetails.label : null,
            score: developmentMatchDetails ? developmentMatchDetails.score : null,
          };
        });
      }
      const sortedSkills = [
        ...skills.filter(({ matchDetails }) => matchDetails),
        ...skills.filter(({ matchDetails, developmentMatchDetails }) => developmentMatchDetails && !matchDetails),
        ...developmentSkills.filter(({ developmentMatchDetails }) => developmentMatchDetails),
        ...skills.filter(({ matchDetails, developmentMatchDetails }) => !developmentMatchDetails && !matchDetails),
      ];
      if (size) {
        if (sortedSkills.length <= size) {
          return sortedSkills;
        }
        const remainder = {
          background: '#eee',
          name: `+${sortedSkills.length - size}`,
        };
        return [ ...sortedSkills.slice(0, size), remainder ];
      }
      return sortedSkills;
    },
    formatDate,
    async applySubmit(application) {
      try {
        this.application = application;
        const { termsAgreed, CV, message } = this.application;
        await this.$apollo.mutate({
          mutation: JobApplicationCreateMutation,
          variables: {
            create: {
              job: { id: this.job.id },
              user: { id: this.user.id },
              fullName: this.user.fullName,
              emailAddress: this.user.emailAddress,
              phoneNumber: '',
              source: 'JOB',
              isInternal: true,
              resume: CV,
              termsAgreed,
              message
            },
          },
        });

        this.applyResult = 'OK';
      } catch (err) {
        this.$log.error(err);
        this.applyResult = 'ERR';
      }
    },
    matchMentors(mentors, job) {
      return mentors.filter(mentor => mentorToCurrentJobMatchScore.call(this, mentor, job) > 0).sort((a, b) => {
        return -1 * (mentorToCurrentJobMatchScore.call(this, a, job) - mentorToCurrentJobMatchScore.call(this, b, job));
      });
    },
    matchRoles(roles, job) {
      return roles.filter(role => roleToCurrentJobMatchScore.call(this, role, job) > 0 && role.id !== job.id).sort((a, b) => {
        return -1 * (roleToCurrentJobMatchScore.call(this, a, job) - roleToCurrentJobMatchScore.call(this, b, job));
      });
    },
    async submitContactMentorRequest(form) {
      const { mentor, form: { message, selectedSkills: skills } } = form;
      const create = {
        message,
        skills,
        mentor: {
          id: mentor.id,
        },
      };
      try {
        const { data } = await this.$apollo.mutate({
          mutation: MentorRequestCreateMutation,
          variables: { create },
          update(store, { data: { createMentorRequest } }) {
            const data = store.readQuery({ query: InternalJobShowQuery });
            data.user.mentorRequests.push(createMentorRequest);
            store.writeQuery({ query: InternalJobShowQuery, data });
          },
        });
        console.log('SUCCESS: ', data);
        Promise.resolve(this.$root.captureLog({
          eventName: 'IM_NEW_MENTOR_REQUEST',
          user: { id: this.user.id },
          data: [ { key: 'mentorID', value: mentor.id } ],
        }));
      } catch (error) {
        console.error(error);
      }
    },
    async sendContactEmail({ contactForm, talent }) {
      console.log({ contactForm, talent });
      try {
        this.isLoading = true;
        const { id: userID } = talent;

        // Manually check we have a userID to send to, otherwise we risk sending to everyone
        this.$assert(userID, new Error('Missing userID'));

        const email = {
          template: this.getEmailBody(contactForm, talent),
          query: emailQuery,
          where: { users: { id: userID } },
        };

        if (contactForm.fromName && contactForm.fromName.length) {
          email.fromName = contactForm.fromName;
        }

        await this.$apollo.mutate({
          mutation: SendEmailMutation,
          variables: { email },
        });
        this.isLoading = false;
        setTimeout(() => this.$refs.testEmailSentSucessfullyModal.show(), 300);
      } catch (err) {
        this.$log.error(err);
        this.isLoading = false;
        setTimeout(() => this.$refs.emailSendErrorModal.show(), 300);
      }
    },

    getEmailBody({ message, fromName, subject: subjectEnum }, talent) {

      const { siteBranding, sitePreferences } = this.company || {};
      const { emailHeaderImage, primaryColour, secondaryColour, backgroundColour, headerFont, bodyFont } = siteBranding || {};
      const { emailHideBrandLogo } = sitePreferences || {};

      const subject = subjectEnum === 'RECOMMEND_JOB' ? 'Recommended opportunity' : `${fromName} wants to contact you`;

      const brandingSettings = {
        headerImage: emailHeaderImage ? emailHeaderImage.fileLocation : '',
        hideBrandLogo: emailHideBrandLogo === true,
        primaryColour,
        secondaryColour,
        backgroundColour,
        bodyFont,
        headerFont,
      };

      const slots = [
        {
          content: message,
        }
      ];

      const params = {
        subject,
        slots,
        brandingSettings,
        jobID: this.job.id,
        jobTitle: this.job.title,
      };

      this.$assert(recommendJobEmailTemplate && typeof recommendJobEmailTemplate.mjml === 'function', 'Template not found');
      this.$assert(genericContactEmailTemplate && typeof genericContactEmailTemplate.mjml === 'function', 'Template not found');

      return subjectEnum === 'RECOMMEND_JOB' ? recommendJobEmailTemplate.mjml.call(this, params) : genericContactEmailTemplate.mjml.call(this, params);
    },
  },
};
</script>
<style lang="scss">

#profile-show-modal .gray-bg {
  background: #f9f9f9;
}

.feedback-icon-line {
  font-size: 60px;
  text-align: center;

  &.success {
    color: #27ae60;
  }

  &.error {
    color: #e74c3c;
  }
}

.feedback-text-line {
  font-size: 14px;
  text-align: center;
}

.page .internal-job-show {

  .table th {
    border-top: 0px !important;
    border-bottom: 1px solid #dee2e6 !important;
  }

  .development-tag {
    position: relative;
    display: inline-block;
    padding: 3px 10px;
    border-radius: 2px;
    font-size: 14px;
    bottom: 2px;
  }

  .block-title {
    text-transform: uppercase;
    font-weight: 600;
  }

  .loading-panel {
    margin: 20px 0 20px 0;
    text-align: center;
  }

  .horizontal-filter {
    margin-top: 1.4em;
  }
  .alert-danger {
    color: #721c24;
    background-color: #f8d7da;
    border-color: #f5c6cb;
  }

  .card {
    &.job-card {
      @media (min-width: 768px) {
        border-right: 0;
      }
      @media (max-width: 768px) {
        border-bottom: 0;
      }
    }

    .card-footer {
      text-align: center;
      h4 {
        vertical-align: middle;
      }
    }

    .job-edit-button {
      position: absolute;
      right: 15px;
    }

    .job-image {
      display: block;
      max-width: 150px;
      max-height: 150px;
      width: auto;
      height: auto;
    }

    .job-description {
      flex-direction: column;
      h1 {
        font-size: 1.3em;
      }
      h2 {
        font-size: 1.2em;
      }
    }

    .tags {
      display: inline-flex;
      .match-label {
        padding-left: 0.5em;
        padding-right: 0.5em;
        width: inherit;
        font-weight: 400;
        text-transform: inherit;
        margin-right: 0.7em;
        height: inherit;
      }
    }
  }

  .contacts-list {
    padding-top: 1em;
    display: flex;
    flex-direction: row;
    flex-flow: row wrap;
    margin-left: -1em;
    margin-right: -1em;
  }

  .filter-row {
    &.no-results {
      text-align: center;
    }
    .question-circle {
      margin: 0 auto;
      width: 80px;
      height: 80px;
      border-radius: 100%;
      color: white;
      vertical-align: middle;
      font-size: 50px;
      font-weight: bolder;
      span {
        text-align: center;
      }
    }
  }

  .contact-col {
    flex-basis: calc(100%/3);
    padding: 1em;
  }
  .upload-selector {
    max-width: 650px;
    margin: 0 auto;
    .btn:hover {
      opacity: 0.8;
    }
  }

  .sticky-top {
    top: 10px;
    z-index: 900;
  }

  .skill-tag {
    display: inline-block;
    font-size: 13px;
    padding: 3px 6px;
    border-radius: 2px;
  }
  .development-tag {
    position: relative;
    display: inline-block;
    padding: 5px 12px;
    border-radius: 2px;
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/jobs/InternalJobShow.vue